"use client";

import { useActionState, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { applyAction } from "@/app/actions/leads";

const BUDGET_OPTIONS = [
  "Under $5,000",
  "$5,000 - $15,000",
  "$15,000 - $50,000",
  "$50,000 - $100,000",
  "$100,000+",
];

export function ApplyForm() {
  const [state, formAction, pending] = useActionState(applyAction, undefined);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [budget, setBudget] = useState("");

  const filled = [fullName, email, businessName, budget].filter(Boolean).length;
  const pct = Math.round((filled / 4) * 100);
  const canSubmit = Boolean(fullName && email && businessName && budget);

  if (state?.ok) {
    return (
      <div className="min-h-screen bg-cl-bg flex items-center justify-center px-4 text-center">
        <div>
          <CheckCircle2 className="w-14 h-14 text-cl-green mx-auto mb-5" />
          <h1 className="font-display font-bold text-3xl text-cl-text mb-3">
            Application Received
          </h1>
          <p className="text-cl-muted text-sm mb-6 max-w-sm mx-auto">
            Our team reviews every application personally. You&apos;ll hear back
            within 48 hours.
          </p>
          <p className="font-mono text-xs text-cl-muted">
            REF: CL-{Date.now().toString(36).toUpperCase()}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cl-bg">
      <div className="max-w-xl mx-auto px-4 lg:px-8 pt-32 pb-24">
        <div className="mb-10">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono text-cl-green uppercase tracking-widest">
              Creating Your Profile
            </span>
            <span className="text-[10px] font-mono text-cl-muted">{pct}%</span>
          </div>
          <div className="w-full h-px bg-cl-border">
            <div
              className="h-full bg-cl-green transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        <h1 className="font-display font-bold text-3xl lg:text-4xl text-cl-text mb-3">
          Get Founding Access
        </h1>
        <p className="text-cl-muted text-sm mb-10">
          Founding rates are permanent. This is the least the platform will ever
          cost.
        </p>

        <form action={formAction} className="space-y-8">
          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Full Name <span className="text-cl-green">*</span>
            </label>
            <input
              type="text"
              name="full_name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Your full name"
              required
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Email <span className="text-cl-green">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Business Name <span className="text-cl-green">*</span>
            </label>
            <input
              type="text"
              name="business_name"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your business or store name"
              required
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-3">
              Monthly Buying Budget <span className="text-cl-green">*</span>
            </label>
            <input type="hidden" name="budget" value={budget} />
            <div className="grid grid-cols-2 gap-2">
              {BUDGET_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setBudget(option)}
                  className={`px-3 py-3 text-xs rounded border text-left transition-colors font-mono ${
                    budget === option
                      ? "border-cl-green bg-cl-green/10 text-cl-green"
                      : "border-cl-border text-cl-muted hover:border-cl-muted"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Categories of Interest
            </label>
            <input
              type="text"
              name="categories"
              placeholder="Sneakers, Streetwear..."
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Where You Sell
            </label>
            <input
              type="text"
              name="sales_channels"
              placeholder="Amazon, eBay, Shopify..."
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors"
            />
          </div>

          <div>
            <label className="block text-[10px] font-mono text-cl-muted uppercase tracking-widest mb-2">
              Anything else?
            </label>
            <textarea
              name="message"
              rows={3}
              placeholder="Tell us about your operation..."
              className="w-full bg-transparent border-0 border-b border-cl-border pb-2 text-cl-text text-base placeholder:text-cl-muted/20 focus:border-cl-green focus:outline-none transition-colors resize-none"
            />
          </div>

          {state?.error ? (
            <p className="text-center text-[10px] text-red-400 font-mono">
              {state.error}
            </p>
          ) : null}

          <button
            type="submit"
            disabled={pending || !canSubmit}
            className="group w-full flex items-center justify-center gap-2 py-4 bg-cl-green text-cl-bg font-display font-bold text-sm rounded hover:bg-cl-green/90 transition-colors disabled:opacity-40"
          >
            {pending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                Submit Application{" "}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </>
            )}
          </button>

          <p className="text-center text-[10px] text-cl-muted font-mono">
            Reviewed personally within 48 hours. Founding rates locked forever.
          </p>
        </form>
      </div>
    </div>
  );
}
